import { inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { Account } from 'projects/shared/src/lib/core/interfaces/account.interface';
import { AuthInfo } from 'projects/shared/src/lib/core/interfaces/auth-info.interface';
import { AccountService } from 'projects/shared/src/lib/core/services/account.service';
import { AuthService } from 'projects/shared/src/lib/core/services/auth.service';
import { LocalStorageService } from 'projects/shared/src/lib/core/services/local-storage.service';
import {
  catchError,
  combineLatest,
  filter,
  firstValueFrom,
  from,
  interval,
  map,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  timeout,
} from 'rxjs';
import { withHydration } from './features/with-hydration.feature';

const STORAGE_KEY = 'AUTH_INFO';
const REFRESH_AUTH_INFO_INTERVAL = 30000; // Check every 30 seconds
const EMPTY_AUTH_INFO: AuthInfo = {
  isAuthenticated: false,
  accessToken: '',
  refreshToken: '',
  idToken: '',
  errorMessage: '',
  userData: undefined,
};

enum AuthStatus {
  Unauthenticated,
  Authenticating,
  Authenticated,
  RefreshToken,
}

class AuthStreamsUtil {
  private readonly _authInfo: ReplaySubject<AuthInfo> = new ReplaySubject<AuthInfo>(1);
  private readonly _accountInfo: ReplaySubject<Account | null> = new ReplaySubject<Account | null>(
    1
  );
  private readonly _invalidateRefreshTokenSubscription: Subject<boolean> = new Subject<boolean>();
  private _tokenRefreshLoopSubscription: Subscription | null = null;

  public authInfo$ = this._authInfo.asObservable();
  public accountInfo$ = this._accountInfo.asObservable();
  public refreshInterval = REFRESH_AUTH_INFO_INTERVAL;

  public nextAuthInfo(x: AuthInfo) {
    this._authInfo.next(x);
  }

  public nextAccountInfo(x: Account | null) {
    this._accountInfo.next(x);
  }

  public subscribe(
    refreshTokenStream: Observable<AuthInfo>,
    tokenRefreshedHandler: (x: AuthInfo) => void
  ) {
    this.invalidate();
    this._tokenRefreshLoopSubscription = interval(this.refreshInterval)
      .pipe(
        takeUntil(this._invalidateRefreshTokenSubscription.asObservable()),
        switchMap(() => combineLatest([this.authInfo$, refreshTokenStream])),
        filter(
          ([currentInfo, nextInfo]) =>
            // Allow refresh if next info is authenticated, even if current is not
            // This prevents blocking refresh when auth state is temporarily cleared
            nextInfo.isAuthenticated &&
            (!currentInfo.accessToken || // No current token (needs refresh)
              !nextInfo.accessToken || // No next token (auth failed)
              currentInfo.accessToken !== nextInfo.accessToken) // Token changed
        ),
        map(([, nextInfo]) => nextInfo)
      )
      .subscribe((authInfo) => {
        tokenRefreshedHandler(authInfo);
      });
  }

  public isSubscribed() {
    return this._tokenRefreshLoopSubscription !== null;
  }

  public invalidate() {
    if (this._tokenRefreshLoopSubscription) {
      try {
        this._tokenRefreshLoopSubscription.unsubscribe();
      } finally {
        this._invalidateRefreshTokenSubscription.next(true);
        this._tokenRefreshLoopSubscription = null;
      }
    }
  }
}

const syncUtil = new AuthStreamsUtil();

type AuthState = {
  account: Account | null;
  authInfo: AuthInfo;
  _status: AuthStatus;
  _authAttempts: number;
};

const initialState: AuthState = {
  account: null,
  authInfo: EMPTY_AUTH_INFO,
  _status: AuthStatus.Unauthenticated,
  _authAttempts: 0,
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withHydration(STORAGE_KEY, initialState),
  withProps(() => ({
    _syncUtil: syncUtil,
  })),
  withComputed((store) => ({
    isAuthenticated: store.authInfo.isAuthenticated,
  })),
  withMethods(
    (
      store,
      localStorageService = inject(LocalStorageService),
      authService = inject(AuthService),
      accountService = inject(AccountService)
    ) => ({
      async refreshAuthInfo(): Promise<AuthInfo> {
        const { isAuthenticated, accessToken, idToken, errorMessage, userData } =
          await authService.checkAutharize();
        const refreshToken = await authService.getRefreshToken();
        const authInfo = {
          isAuthenticated,
          accessToken,
          refreshToken,
          idToken,
          errorMessage,
          userData,
        };

        this._setAuthInfo(authInfo);

        return authInfo;
      },
      async authorize(): Promise<void> {
        try {
          await this.refreshAuthInfo();

          if (store.authInfo.isAuthenticated()) {
            this._startForehandTokenRefreshLoop();
          } else {
            // fallback in case of infinite loop (shouldn't hapen normally, but just in case)
            if (store._status() === AuthStatus.Authenticating) {
              patchState(store, { _authAttempts: store._authAttempts() + 1 });
              if (store._authAttempts() > 10) {
                patchState(store, { _authAttempts: 0 });
                this.logoutLocal();
                return;
              }
            }

            this._resetLoops();
            patchState(store, { _status: AuthStatus.Authenticating });
            authService.authorize();
          }
        } catch {
          patchState(store, { _status: AuthStatus.Unauthenticated });
          this._resetAuthInfo();
        }
      },
      refreshToken(): Observable<AuthInfo> {
        const status = store._status(); // debounce multiple calls
        if (status !== AuthStatus.RefreshToken && status !== AuthStatus.Authenticating) {
          if (store.isAuthenticated()) {
            patchState(store, { _status: AuthStatus.RefreshToken });

            // DO NOT reset auth info before refresh - this causes users to appear logged out
            return authService.forceRefreshSession().pipe(
              take(1),
              map((x) => ({
                isAuthenticated: x.isAuthenticated,
                accessToken: x.accessToken,
                idToken: x.idToken,
                errorMessage: x.errorMessage,
                userData: x.userData,
              })),
              switchMap(authInfo => from(authService.getRefreshToken())
                .pipe(map(refreshToken => ({ ...authInfo, refreshToken })))
              ),
              tap((x) => {
                this._setAuthInfo(x);
              }),
              catchError((err) => {
                patchState(store, { _status: AuthStatus.Unauthenticated });
                this._resetAuthInfo();
                return throwError(() => new Error(err));
              })
            );
          } else {
            return from(this.refreshAuthInfo());
          }
        }

        return store._syncUtil.authInfo$.pipe(filter((x) => x.isAuthenticated));
      },
      isAuthenticating() {
        return store._status() === AuthStatus.Authenticating;
      },
      async waitForAuthReadyAsync(timeoutInterval?: number, caller?: string) {
        try {
          await this.refreshAuthInfo();
          await (timeoutInterval
            ? firstValueFrom(
              store._syncUtil.authInfo$.pipe(
                filter((x) => x.isAuthenticated),
                timeout(timeoutInterval!)
              )
            )
            : firstValueFrom(store._syncUtil.authInfo$.pipe(filter((x) => x.isAuthenticated))));

          return true;
        } catch {
          if (timeoutInterval)
            console.log(
              `${caller ? caller + ': ' : ''}auth timeout excceded ${timeoutInterval / 1000} s`
            );
          return false;
        }
      },
      async waitForAccountReadyAsync(timeoutInterval?: number, caller?: string) {
        try {
          await (timeoutInterval
            ? firstValueFrom(
              store._syncUtil.accountInfo$.pipe(
                filter((x) => !!x),
                timeout(timeoutInterval!)
              )
            )
            : firstValueFrom(store._syncUtil.accountInfo$.pipe(filter((x) => !!x))));

          return true;
        } catch {
          if (timeoutInterval)
            console.log(
              `${caller ? caller + ': ' : ''}account timeout excceded ${timeoutInterval / 1000} s`
            );

          return false;
        }
      },
      logoutLocal() {
        patchState(store, { _status: AuthStatus.Unauthenticated });
        this._resetLoops();
        this._resetAuthInfo();
        this._setAccountInfo(null);

        authService.logoutLocal();
      },
      // Use this method when need to logout from the app and re-login with current Lexipoort session
      // Examples: after application restart, if last lexipoort session is active but user hasn't a product license
      async logout() {
        patchState(store, { _status: AuthStatus.Unauthenticated });
        this._resetLoops();
        this._resetAuthInfo();
        this._setAccountInfo(null);

        await authService.logout();
      },
      // Use this method when need to logout from the application and from Lexipoort and change the current user
      async centralLogout(): Promise<void> {
        patchState(store, { _status: AuthStatus.Unauthenticated });
        this._resetLoops();
        this._resetAuthInfo();
        this._setAccountInfo(null);

        await authService.centralLogout();
      },
      async requestAccount() {
        const account = await accountService.requestCurrent();
        this._setAccountInfo(account);
        return account;
      },
      _setAuthInfo(authInfo: AuthInfo) {
        if (authInfo.isAuthenticated) {
          patchState(store, { _status: AuthStatus.Authenticated, _authAttempts: 0 });
        }
        patchState(store, { authInfo: { ...authInfo } });
        store._syncUtil.nextAuthInfo(store.authInfo());
      },
      _setAccountInfo(account: Account | null) {
        if (account) {
          patchState(store, { account: { ...account } });
        } else {
          patchState(store, { account: null });
        }
        store._syncUtil.nextAccountInfo(account);
      },
      _resetAuthInfo() {
        this._setAuthInfo(EMPTY_AUTH_INFO);
      },
      _resetLoops() {
        store._syncUtil.invalidate();
      },
      _startForehandTokenRefreshLoop() {
        if (!store._syncUtil.isSubscribed()) {
          // debounce multiple calls
          store._syncUtil.subscribe(
            from(authService.checkAutharize()).pipe(
              map((x) => ({
                isAuthenticated: x.isAuthenticated,
                accessToken: x.accessToken,
                idToken: x.idToken,
                errorMessage: x.errorMessage,
                userData: x.userData,
              })),
              switchMap(authInfo => from(authService.getRefreshToken())
                .pipe(map(refreshToken => ({ ...authInfo, refreshToken }))))
            ),
            (nextInfo) => {
              const { isAuthenticated, accessToken, idToken, errorMessage, userData } = nextInfo;
              this._setAuthInfo({
                ...EMPTY_AUTH_INFO,
                isAuthenticated,
                accessToken,
                idToken,
                errorMessage,
                userData,
              });
            }
          );
        }
      },
    })
  ),
  withHooks({
    onDestroy(store) {
      store._resetLoops();
    },
  })
);
